import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { Send, Paperclip, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILE_MB = 10;

const emptyForm = () => ({
  name: '',
  phone: '',
  email: '',
  company: '',
  website: '',
  message: ''
});

const QuoteForm = ({ source = 'Quote form' }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(emptyForm());
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      toast({ title: 'File too large', description: `Please attach a file under ${MAX_FILE_MB}MB or email it to hello@yellowagency.co.uk.` });
      e.target.value = '';
      return;
    }
    setFile(f);
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Missing details', description: 'Please add your name, email and a short brief.' });
      return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      data.append('name', form.name);
      data.append('email', form.email);
      if (form.phone) data.append('phone', form.phone);
      if (form.company) data.append('company', form.company);
      if (form.website) data.append('website', form.website);
      data.append('message', form.message);
      data.append('source', source);
      if (file) data.append('attachment', file);

      await axios.post(`${API}/enquiry`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast({ title: 'Thanks! Your bespoke proposal will be with you within 48 hours.', description: `We’ve received your enquiry, ${form.name}.` });
      setForm(emptyForm());
      clearFile();
    } catch (err) {
      toast({ title: 'Something went wrong', description: err?.response?.data?.detail || 'Please try again in a moment, or email hello@yellowagency.co.uk.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-[#111114] border border-white/10 rounded-2xl p-7 lg:p-9">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Your name" value={form.name} onChange={setField('name')} testid="quote-name" />
        <Field label="Your phone" value={form.phone} onChange={setField('phone')} testid="quote-phone" />
        <Field label="Your email" type="email" value={form.email} onChange={setField('email')} testid="quote-email" />
        <Field label="Your company" value={form.company} onChange={setField('company')} testid="quote-company" />
        <div className="md:col-span-2">
          <Field label="Website URL" value={form.website} onChange={setField('website')} testid="quote-website" />
        </div>
        <div className="md:col-span-2">
          <label className="section-label">Tell us about your current marketing issues and what your goals are for your campaigns</label>
          <textarea
            data-testid="quote-message"
            rows={6}
            value={form.message}
            onChange={setField('message')}
            placeholder="Where are you now, where do you want to get to, and what have you already tried?"
            className="mt-2 w-full bg-[#0b0b0d] border border-white/10 rounded-md px-4 py-3 text-white text-sm focus:border-[#F5CD1B] focus:outline-none focus:ring-2 focus:ring-[#F5CD1B]/20"
          />
        </div>

        {/* File upload */}
        <div className="md:col-span-2">
          <label className="section-label">Upload a brief, document or image (optional)</label>
          <div className="mt-2">
            <input
              ref={fileInputRef}
              type="file"
              onChange={onFileChange}
              data-testid="quote-attachment"
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.gif,.txt,.rtf"
              className="hidden"
            />
            {!file ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="quote-attachment-btn"
                className="w-full flex items-center gap-3 px-4 py-3 rounded-md border border-dashed border-white/15 bg-[#0b0b0d] text-white/70 text-sm hover:border-[#F5CD1B]/50 hover:text-white transition-colors"
              >
                <Paperclip size={16} className="text-[#F5CD1B]" />
                Attach a file (max {MAX_FILE_MB}MB)
              </button>
            ) : (
              <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-md border border-[#F5CD1B]/30 bg-[#F5CD1B]/[0.06] text-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <Paperclip size={16} className="text-[#F5CD1B] shrink-0" />
                  <span className="text-white truncate">{file.name}</span>
                  <span className="text-white/50 text-xs shrink-0">({(file.size / 1024 / 1024).toFixed(1)}MB)</span>
                </div>
                <button type="button" onClick={clearFile} className="text-white/60 hover:text-white p-1" aria-label="Remove file">
                  <X size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <button
        data-testid="quote-submit"
        type="submit"
        disabled={loading}
        className="btn-yellow inline-flex items-center gap-2 mt-6 px-6 py-4 rounded-md uppercase text-sm disabled:opacity-60"
      >
        {loading ? 'Sending...' : (<><Send size={16} /> Request my bespoke proposal</>)}
      </button>
    </form>
  );
};

const Field = ({ label, value, onChange, type = 'text', testid }) => (
  <div>
    <label className="section-label">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={onChange}
      className="mt-2 w-full bg-[#0b0b0d] border border-white/10 rounded-md px-4 h-12 text-white text-sm focus:border-[#F5CD1B] focus:outline-none focus:ring-2 focus:ring-[#F5CD1B]/20"
    />
  </div>
);

export default QuoteForm;
